// feature/test-cleanup-marker — admin maintenance page. One operation today: cleanup of test
// cases marked with "TEST TEST TEST". Confirmation gate requires the user
// to type "LIMPIAR" before the destructive button enables.
import React, { useEffect, useState } from "react";
import { admin as adminApi } from "../../api/endpoints";
import { useToast } from "../../components/Toast";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";

export default function Maintenance() {
  const toast = useToast();
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  function loadHistory() {
    adminApi.cleanupHistory(20)
      .then(r => setHistory(r.data?.entries || []))
      .catch(() => setHistory([]));
  }
  useEffect(() => { loadHistory(); }, []);

  async function runCleanup() {
    if (confirmText !== "LIMPIAR") return;
    setBusy(true);
    try {
      const r = await adminApi.cleanupTestCases();
      const d = r.data || {};
      if (d.count === 0) {
        toast.push(d.message || "No se encontraron casos de prueba para eliminar.");
      } else {
        toast.push(`${d.message} ${(d.case_numbers || []).join(", ")}`);
      }
      setConfirmText("");
      loadHistory();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al limpiar", "error");
    } finally { setBusy(false); }
  }

  const enabled = confirmText === "LIMPIAR" && !busy;

  return (
    <div className="col" style={{ gap: 24 }}>
      <div>
        <h1 style={{ margin: 0 }}>Mantenimiento</h1>
        <div className="muted" style={{ marginTop: 4 }}>Operaciones administrativas y limpieza del sistema.</div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>🗑 Limpieza de casos de prueba</h2>
        <p style={{ fontSize: 14, lineHeight: 1.5 }}>
          Elimina (borrado lógico) todos los casos cuyo accionante contiene{" "}
          <code style={{ background: "var(--color-hover-bg)", padding: "2px 6px", borderRadius: 4 }}>TEST TEST TEST</code>.
          Es recuperable desde la base de datos.
        </p>
        <div className="glass-section" style={{ marginTop: 12 }}>
          <strong>Convención:</strong> Todos los casos de prueba deben tener{" "}
          <code style={{ background: "var(--accent-mint-bg)", padding: "2px 6px", borderRadius: 4 }}>TEST TEST TEST</code>{" "}
          en el campo accionante. Estos casos se eliminan automáticamente cada noche
          a las 3:00 a. m. (hora de Bogotá), o manualmente desde aquí.
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <label>
              Para confirmar, escriba <strong>LIMPIAR</strong>:
            </label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="LIMPIAR"
              disabled={busy}
              autoComplete="off"
            />
          </div>
          <button
            className={enabled ? "danger" : "secondary"}
            onClick={runCleanup}
            disabled={!enabled}
          >
            {busy && <Spinner color="currentColor" />} Limpiar casos de prueba ahora
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-hairline)" }}>
          <h2 style={{ margin: 0 }}>Historial de limpieza</h2>
        </div>
        {history == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !history.length ? (
          <Empty title="Aún no se han registrado limpiezas" />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Origen</th>
                <th>Usuario</th>
                <th>Casos eliminados</th>
                <th>Detalles</th>
              </tr>
            </thead>
            <tbody>
              {history.map(e => (
                <tr key={e.id}>
                  <td>{fmt(e.executed_at)}</td>
                  <td>
                    {e.triggered_by === "cron"
                      ? <span className="badge mint">Automático</span>
                      : <span className="badge warning">Manual</span>}
                  </td>
                  <td>{e.triggered_user_name || e.triggered_user_email || "—"}</td>
                  <td>
                    {e.error_message
                      ? <span className="badge danger">Error</span>
                      : <strong>{e.cases_deleted_count}</strong>}
                  </td>
                  <td>
                    {e.error_message
                      ? <code style={{ color: "var(--color-danger)" }}>{e.error_message}</code>
                      : (e.case_numbers && e.case_numbers.length > 0)
                        ? <details>
                            <summary style={{ cursor: "pointer", color: "var(--accent-mint)" }}>
                              {e.cases_deleted_count} caso{e.cases_deleted_count === 1 ? "" : "s"}
                            </summary>
                            <ul style={{ marginTop: 8, paddingLeft: 20, fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                              {e.case_numbers.map(cn => <li key={cn}>{cn}</li>)}
                            </ul>
                          </details>
                        : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
